import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

import { useAuth, formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Brand from "@/components/Brand";

// Firm-signup page — spun off from the original /signup so the default
// /signup can host the self-serve owner flow (DaxOps-style) without
// firms losing their existing entrypoint.
export default function SignupFirm() {
    const { signupFirm } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({
        firm_name: "",
        country: "NZ",
        admin_name: "",
        admin_email: "",
        password: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    function set(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function submit(e) {
        e?.preventDefault();
        setErr(null);
        setBusy(true);
        try {
            const res = await signupFirm(form);
            toast.success(`Welcome to ClientOps, ${res.tenant.name}`);
            nav("/app", { replace: true });
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="mb-10 inline-flex items-center gap-2 text-xs text-[#5E636E] hover:text-[#0F1115] transition-colors" data-testid="signup-firm-back-home">
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to home
                    </Link>
                    <Brand size="md" className="mb-8" />
                    <h1 className="font-display text-3xl tracking-tight font-medium">Start your firm on ClientOps</h1>
                    <p className="mt-2 text-sm text-[#5E636E]">
                        Creates a private tenant for your firm.{" "}
                        <Link to="/login" className="text-[#171717] underline underline-offset-4" data-testid="signup-firm-login-link">
                            Already have an account?
                        </Link>
                    </p>

                    <form className="mt-8 space-y-4" onSubmit={submit} data-testid="signup-firm-form">
                        <div className="space-y-1.5">
                            <Label htmlFor="firm_name">Firm name</Label>
                            <Input
                                id="firm_name"
                                placeholder="e.g. Lakeside Tax Co"
                                value={form.firm_name}
                                onChange={(e) => set("firm_name", e.target.value)}
                                required
                                data-testid="signup-firm-name"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="admin_name">Your name</Label>
                            <Input
                                id="admin_name"
                                placeholder="Jane Doe"
                                value={form.admin_name}
                                onChange={(e) => set("admin_name", e.target.value)}
                                required
                                data-testid="signup-firm-admin-name"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="admin_email">Work email</Label>
                            <Input
                                id="admin_email"
                                type="email"
                                placeholder="you@firm.co.nz"
                                value={form.admin_email}
                                onChange={(e) => set("admin_email", e.target.value)}
                                required
                                data-testid="signup-firm-admin-email"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Minimum 8 characters"
                                minLength={8}
                                value={form.password}
                                onChange={(e) => set("password", e.target.value)}
                                required
                                data-testid="signup-firm-password"
                            />
                        </div>
                        {err && (
                            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="signup-firm-error">
                                {err}
                            </div>
                        )}
                        <Button
                            type="submit"
                            className="w-full h-10 bg-[#171717] hover:bg-[#2E2E2E] text-white"
                            disabled={busy}
                            data-testid="signup-firm-submit-button"
                        >
                            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating tenant…</> : "Create my firm tenant"}
                        </Button>
                        <p className="text-[11px] text-[#5E636E]">
                            You become the firm admin. Invite staff and clients from inside.
                        </p>
                        <p className="text-[11px] text-[#5E636E]">
                            Just running your own business?{" "}
                            <Link to="/signup" className="underline underline-offset-4 text-[#171717]" data-testid="signup-firm-owner-link">
                                Sign up as an owner instead →
                            </Link>
                        </p>
                    </form>
                </div>
            </div>
            <div className="hidden lg:flex bg-[#0F1115] text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-dot-grid opacity-[0.08]" />
                <div className="relative z-10 m-auto max-w-md px-12">
                    <div className="co-label !text-white/60 mb-3">What you get</div>
                    <ul className="space-y-5 text-sm text-white/80 leading-relaxed">
                        <li><span className="font-display text-white text-base font-medium block">A private tenant.</span> Branding, isolated data.</li>
                        <li><span className="font-display text-white text-base font-medium block">A structured intake hub.</span> Documents, requests, messages, tasks — all in one place.</li>
                        <li><span className="font-display text-white text-base font-medium block">A client portal.</span> Mobile-first, guided uploads, self-serve help.</li>
                        <li><span className="font-display text-white text-base font-medium block">Review-ready records.</span> Tagged by entity, period and document type.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
